import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import GoogleAuthSetupModal from '../components/Auth/GoogleAuthSetupModal';
import { API_CONFIG } from '../config/api';

interface LoginFormData {
  email: string;
  password: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [signupDisabled] = useState(
    process.env.REACT_APP_ALLOW_PUBLIC_SIGNUP !== 'true'
  ); // Check environment variable
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    company: '',
    rememberMe: false,
  });
  const [errors, setErrors] = useState<Partial<LoginFormData>>({});
  const [darkMode, setDarkMode] = useState(false);
  const [showGoogleSetupModal, setShowGoogleSetupModal] = useState(false);
  // const [devAutoLoginAvailable, setDevAutoLoginAvailable] = useState(false); // DISABLED FOR PRODUCTION

  const { signIn, signUp, signInWithGoogle, refreshAuth } = useAuth();
  const navigate = useNavigate();

  // Check for dev mode auto-login credentials - DISABLED FOR PRODUCTION
  // useEffect(() => {
  //   const checkDevCredentials = async () => {
  //     try {
  //       const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
  //       const response = await fetch(`${API_URL}/api/dev/test-account`);
  //       if (response.ok) {
  //         const data = await response.json();
  //         if (data.success && data.account) {
  //           setDevAutoLoginAvailable(true);
  //           setFormData(prev => ({
  //             ...prev,
  //             email: data.account.email,
  //             password: '••••••••' // Show masked password
  //           }));
  //         }
  //       }
  //     } catch (error) {
  //       // Dev mode not available, continue normally
  //     }
  //   };
  //   
  //   checkDevCredentials();
  // }, []);

  // Detect system theme
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    setDarkMode(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setDarkMode(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  // Dev mode: Auto-fill credentials button handler - DISABLED FOR PRODUCTION
  // const handleDevAutoFill = async () => {
  //   try {
  //     const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
  //     const response = await fetch(`${API_URL}/api/dev/test-account`);
  //     if (response.ok) {
  //       const data = await response.json();
  //       if (data.success && data.account) {
  //         setFormData(prev => ({
  //           ...prev,
  //           email: data.account.email,
  //           password: data.account.password
  //         }));
  //         console.log('✅ Dev credentials auto-filled');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Failed to load dev credentials:', error);
  //   }
  // };

  // Dev mode: Skip onboarding and direct login - DISABLED FOR PRODUCTION
  // const handleDevQuickLogin = async () => {
  //   setIsLoading(true);
  //   try {
  //     const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8082';
  //     const response = await fetch(`${API_URL}/api/dev/quick-login`, {
  //       method: 'POST'
  //     });
  //     
  //     if (response.ok) {
  //       const data = await response.json();
  //       if (data.success) {
  //         // Store auth data
  //         localStorage.setItem('auth', JSON.stringify({
  //           token: data.token,
  //           brandId: data.brand_id,
  //           email: data.email,
  //           fullName: data.full_name
  //         }));
  //         
  //         console.log('✅ Dev quick login successful');
  //         navigate('/dashboard');
  //       }
  //     }
  //   } catch (error) {
  //     console.error('Dev quick login failed:', error);
  //     setErrors({ email: 'Dev mode login failed. Try manual login.' });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const validateForm = (): boolean => {
    const newErrors: Partial<LoginFormData> = {};

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Sign up specific validation
    if (isSignUp) {
      if (!formData.firstName) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.company) {
        newErrors.company = 'Company name is required';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const API_URL = API_CONFIG.BASE_URL;
    
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({}); // Clear previous errors
    
    try {
      if (isSignUp) {
        // Signup flow - DISABLED
        console.log('❌ Signup attempt blocked - public registration disabled');
        setErrors({ 
          email: 'Public registration is disabled. Contact your administrator for account creation.' 
        });
        return;
        
        // Legacy signup code preserved for reference
        /*
        console.log('🔥 Signup attempt starting...');
        console.log('Email:', formData.email);
        console.log('API URL:', API_URL);
        console.log('Endpoint:', API_URL + '/api/v1/auth/register');
        console.log('📤 Sending request to signup endpoint...');
        
        const response = await fetch(API_URL + '/api/v1/auth/register', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
          credentials: 'include',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password,
            first_name: formData.firstName,
            last_name: formData.lastName,
            company_name: formData.company || formData.email.split('@')[0],
            website: `https://${formData.company?.toLowerCase().replace(/\s+/g, '-') || 'example'}.com`
          })
        });
        
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);
        
        if (!response.ok) {
          const errorMessage = data.detail || data.message || 'Signup failed';
          setErrors({ email: errorMessage });
          return;
        }
        
        console.log('🎉 Signup successful! Storing auth token...');
        
        // Store auth token and data
        localStorage.setItem('auth_token', data.token);
        localStorage.setItem('brand_id', data.brand_id);
        localStorage.setItem('user_email', formData.email);
        localStorage.setItem('full_name', `${formData.firstName} ${formData.lastName}`);
        
        // Also store in 'auth' object format for Settings page
        localStorage.setItem('auth', JSON.stringify({
          token: data.token,
          brand_id: data.brand_id,
          brandId: data.brand_id, // Both formats for compatibility
          email: formData.email,
          full_name: `${formData.firstName} ${formData.lastName}`
        }));
        
        // Navigate to onboarding flow for new users
        console.log('🚀 Redirecting to onboarding flow...');
        navigate('/onboarding');
        */
        
      } else {
        // Login flow
        const response = await fetch(API_URL + '/api/v1/auth/login', {
          method: 'POST',
          credentials: 'include', // 🔧 FIX: Include cookies for cross-origin requests
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          mode: 'cors',
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          })
        });
        
        console.log('Response status:', response.status);
        const data = await response.json();
        console.log('Response data:', data);
        
        if (!response.ok) {
          // Show specific error from API
          const errorMessage = data.detail || data.message || 'Authentication failed';
          setErrors({ email: errorMessage });
          return;
        }
        
        console.log('🎯 Login successful! Backend set HTTP-Only cookies');

        // No need to store tokens in localStorage anymore - cookies are automatic
        console.log('✅ Login response:', data);

        // Store brand info in localStorage for use across the app
        const brandId = data.brand_id || data.brandId || null;
        if (brandId) {
          localStorage.setItem('brand_id', brandId);
        }
        if (data.brand_name) {
          localStorage.setItem('brand_name', data.brand_name);
        }
        if (data.email) {
          localStorage.setItem('user_email', data.email);
        }

        // Determine redirect: send new users or incomplete onboarding to /onboarding
        const onboardingCompleteFromApi = data.onboarding_complete;
        const isNewUser = data.is_new_user;
        const localStorageKey = brandId ? `onboarding_complete_${brandId}` : null;
        const onboardingCompleteFromStorage = localStorageKey
          ? localStorage.getItem(localStorageKey)
          : null;

        const shouldOnboard =
          onboardingCompleteFromApi === false ||
          isNewUser === true ||
          (onboardingCompleteFromApi === undefined &&
            isNewUser === undefined &&
            onboardingCompleteFromStorage === null);

        console.log('🔄 Redirecting after login...', { shouldOnboard, brandId });
        window.location.href = shouldOnboard ? '/onboarding' : '/dashboard';
      }
      
    } catch (error: any) {
      console.error('Authentication error:', error);

      let errorMessage = 'Something went wrong. Please try again in a moment.';

      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        // Log technical details for debugging, show friendly message to user
        console.error('Network error:', error.message, '| API:', API_URL);
        errorMessage = 'Unable to reach the server. Please check your internet connection and try again.';
      }

      setErrors({ email: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitOld = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      let result;
      if (isSignUp) {
        const fullName = `${formData.firstName} ${formData.lastName}`;
        result = await signUp(formData.email, formData.password, fullName, formData.company);
      } else {
        result = await signIn(formData.email, formData.password);
      }
      
      if (result.error) {
        // Safely extract error message
        const errorMessage = typeof result.error.message === 'string' 
          ? result.error.message 
          : 'Authentication failed. Please try again.';
        setErrors({ email: errorMessage });
      } else {
        // Redirect to welcome wizard for new signups, dashboard for login
        if (isSignUp) {
          navigate('/welcome', { 
            state: { 
              brandName: formData.company,
              isNewUser: true 
            } 
          });
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      const errorMessage = typeof error === 'string' 
        ? error 
        : error?.message || 'Authentication failed. Please try again.';
      setErrors({ email: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'microsoft') => {
    if (provider === 'google') {
      // Check if Google OAuth is properly configured
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      console.log('Google Client ID from env:', clientId);
      
      // Check for invalid/demo client IDs that need setup
      const needsSetup = !clientId || 
        clientId === 'demo-google-client-id.apps.googleusercontent.com' ||
        clientId === 'your-google-client-id-here' ||
        clientId === 'your-google-client-id' ||
        clientId.includes('your-google-client-id') ||
        clientId.includes('demo-google-client-id');
      
      console.log('Needs setup:', needsSetup);
      
      if (needsSetup) {
        console.log('Showing Google setup modal');
        setShowGoogleSetupModal(true);
        return;
      }
      
      console.log('Google OAuth configured, proceeding with authentication');
    }

    setIsLoading(true);
    try {
      if (provider === 'google') {
        const result = await signInWithGoogle();
        if (result.error) {
          // Check for common Google OAuth errors
          if (result.error.includes('not loaded') || result.error.includes('not displayed')) {
            setShowGoogleSetupModal(true);
          } else {
            setErrors({ email: `Google authentication failed: ${result.error}` });
          }
        } else {
          navigate('/dashboard');
        }
      } else if (provider === 'microsoft') {
        // Mock Microsoft login for now
        await new Promise(resolve => setTimeout(resolve, 1500));
        navigate('/dashboard');
      }
    } catch (error) {
      console.error(`${provider} authentication error:`, error);
      if (provider === 'google') {
        setShowGoogleSetupModal(true);
      } else {
        setErrors({ email: `${provider} authentication failed` });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const themeClass = darkMode ? 'dark' : 'light';

  return (
    <div className={`min-h-screen flex ${themeClass}`}>
      {/* Left Panel - Marketing & Features */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-white rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-96 h-96 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-75"></div>
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 py-16 text-white">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm mr-4">
                <span className="text-2xl">🤖</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold">AI Commerce OS</h1>
                <p className="text-white/80">Transform Your D2C Business with AI</p>
              </div>
            </div>
          </div>

          {/* Value Proposition */}
          <div className="mb-12">
            <h2 className="text-4xl font-bold mb-6 leading-tight">
              Turn Every Customer Interaction Into Revenue
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Join 500+ D2C brands using AI to increase conversions by 40% and 
              provide 24/7 intelligent customer support.
            </p>
            
            {/* Free Trial Callout */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center mb-3">
                <span className="text-2xl mr-3">🎉</span>
                <h3 className="text-xl font-semibold">Start Your Free Trial</h3>
              </div>
              <p className="text-white/90 mb-4">
                14 days free • No credit card required • Cancel anytime
              </p>
              <div className="flex items-center text-sm text-white/80">
                <span className="mr-4">✓ Full platform access</span>
                <span>✓ Premium support</span>
              </div>
            </div>
          </div>

          {/* Key Features */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold mb-6">Platform Features</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm">🧠</span>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Brand Copilot</h4>
                  <p className="text-white/80 text-sm">AI-powered business intelligence and internal insights</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm">🛍️</span>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Shopper Concierge</h4>
                  <p className="text-white/80 text-sm">24/7 AI shopping assistant for customer interactions</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mr-4 mt-1">
                  <span className="text-sm">📊</span>
                </div>
                <div>
                  <h4 className="font-semibold mb-1">Smart Analytics</h4>
                  <p className="text-white/80 text-sm">Real-time insights and conversion optimization</p>
                </div>
              </div>
            </div>
          </div>

          {/* Customer Testimonials */}
          <div className="mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full flex items-center justify-center text-white font-bold mr-4">
                  JD
                </div>
                <div>
                  <h4 className="font-semibold">Jane Doe</h4>
                  <p className="text-white/70 text-sm">CEO, StyleCorp Fashion</p>
                </div>
              </div>
              <p className="text-white/90 mb-4 italic">
                "AI Commerce OS increased our conversion rate from 2.1% to 3.4% in just 2 months. 
                The AI assistant handles 80% of our customer inquiries automatically."
              </p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-green-300 font-semibold">+40% Conversion Rate</span>
                <span className="text-blue-300 font-semibold">80% Query Automation</span>
              </div>
            </div>
          </div>

          {/* Trust Indicators */}
          <div className="flex items-center justify-between">
            <div className="text-center">
              <div className="text-2xl font-bold">500+</div>
              <div className="text-white/70 text-sm">Active Brands</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">99.9%</div>
              <div className="text-white/70 text-sm">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-lg">🔒</span>
              </div>
              <div className="text-white/70 text-sm">SOC 2 Compliant</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel - Authentication Form */}
      <div className={`flex-1 flex items-center justify-center px-8 py-12 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="w-full max-w-md">
          {/* Mobile Header (visible on small screens) */}
          <div className="lg:hidden text-center mb-8">
            <div className={`w-16 h-16 ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4`}>
              <span className="text-3xl">🤖</span>
            </div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>AI Commerce OS</h1>
            <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Transform Your Business with AI</p>
          </div>

          {/* Form Container */}
          <div className={`${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-2xl shadow-2xl border backdrop-blur-sm p-8`}>
            {/* Form Header */}
            <div className="text-center mb-8">
              <h2 className={`text-3xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                {isSignUp ? 'Account Creation Disabled' : 'Welcome Back'}
              </h2>
              <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isSignUp 
                  ? 'Public registration is disabled. Contact your administrator for account access.' 
                  : 'Sign in to your AI Commerce OS dashboard'
                }
              </p>
            </div>
            
            {/* Signup Disabled Notice */}
            {isSignUp && (
              <div className={`mb-6 p-4 rounded-xl border-2 ${
                darkMode 
                  ? 'border-orange-500/30 bg-orange-500/5 text-orange-300' 
                  : 'border-orange-300 bg-orange-50 text-orange-800'
              }`}>
                <div className="flex items-center mb-2">
                  <span className="text-2xl mr-3">🔒</span>
                  <h3 className="font-semibold">Registration Restricted</h3>
                </div>
                <p className="text-sm mb-3">
                  Self-registration has been disabled for security. All new accounts are created through administrator-controlled onboarding.
                </p>
                <div className="text-xs opacity-75">
                  <strong>Need an account?</strong> Contact your system administrator or use the contact form below.
                </div>
              </div>
            )}

            {/* Social Login Buttons */}
            <div className="space-y-3 mb-6">
              <button
                type="button"
                onClick={() => handleSocialLogin('google')}
                disabled={isLoading}
                className={`w-full flex items-center justify-center px-4 py-3 border rounded-xl font-medium transition-all duration-200 ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
              <button
                type="button"
                onClick={() => handleSocialLogin('microsoft')}
                disabled={isLoading}
                className={`w-full flex items-center justify-center px-4 py-3 border rounded-xl font-medium transition-all duration-200 ${
                  darkMode 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                  <path fill="#f25022" d="M1 1h10v10H1z"/>
                  <path fill="#00a4ef" d="M13 1h10v10H13z"/>
                  <path fill="#7fba00" d="M1 13h10v10H1z"/>
                  <path fill="#ffb900" d="M13 13h10v10H13z"/>
                </svg>
                Continue with Microsoft
              </button>
            </div>

            {/* Dev Mode Quick Login - DISABLED FOR PRODUCTION */}
            {/* {!isSignUp && devAutoLoginAvailable && (
              <div className={`mb-6 p-4 rounded-xl border-2 ${
                darkMode 
                  ? 'border-green-500/30 bg-green-500/5' 
                  : 'border-green-300 bg-green-50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                      darkMode ? 'bg-green-500/20' : 'bg-green-100'
                    }`}>
                      <span className="text-green-600 text-sm">⚡</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Dev Mode - Skip Onboarding
                      </h4>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Auto-login with saved test account
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleDevQuickLogin}
                    disabled={isLoading}
                    className={`px-4 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                      darkMode
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    } focus:outline-none focus:ring-2 focus:ring-green-500/50 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isLoading ? 'Logging in...' : 'Quick Login ⚡'}
                  </button>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div>
                    <span className={`block font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Saved Account:
                    </span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} font-mono`}>
                      {formData.email}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={handleDevAutoFill}
                    className={`text-xs ${darkMode ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-700'} underline`}
                  >
                    Auto-fill credentials
                  </button>
                </div>
              </div>
            )} */}

            {/* Demo Credentials Box (Login Only) */}
            {!isSignUp && (
              <div className={`mb-6 p-4 rounded-xl border-2 border-dashed ${
                darkMode 
                  ? 'border-indigo-500/30 bg-indigo-500/5' 
                  : 'border-indigo-300 bg-indigo-50'
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${
                      darkMode ? 'bg-indigo-500/20' : 'bg-indigo-100'
                    }`}>
                      <span className="text-indigo-600 text-sm">🧪</span>
                    </div>
                    <div>
                      <h4 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Demo Account
                      </h4>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Try the platform instantly
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        email: 'demo@aicommerce.com',
                        password: 'demo123456'
                      }));
                    }}
                    className={`px-4 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                      darkMode
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    } focus:outline-none focus:ring-2 focus:ring-indigo-500/50`}
                  >
                    Auto-Fill Credentials
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className={`block font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Email:
                    </span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} font-mono`}>
                      demo@aicommerce.com
                    </span>
                  </div>
                  <div>
                    <span className={`block font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Password:
                    </span>
                    <span className={`${darkMode ? 'text-gray-400' : 'text-gray-600'} font-mono`}>
                      demo123456
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Divider */}
            <div className="relative mb-6">
              <div className={`absolute inset-0 flex items-center`}>
                <div className={`w-full border-t ${darkMode ? 'border-gray-600' : 'border-gray-300'}`} />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className={`px-2 ${darkMode ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>
                  Or continue with email
                </span>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {/* Sign Up Fields */}
              {isSignUp && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                          errors.firstName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : darkMode
                            ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                            : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                        } focus:ring-4 focus:outline-none`}
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-sm text-red-500">{errors.firstName}</p>
                      )}
                    </div>
                    <div>
                      <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                          errors.lastName
                            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                            : darkMode
                            ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                            : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                        } focus:ring-4 focus:outline-none`}
                        placeholder="Doe"
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-sm text-red-500">{errors.lastName}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                        errors.company
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : darkMode
                          ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                          : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                      } focus:ring-4 focus:outline-none`}
                      placeholder="Your Company"
                    />
                    {errors.company && (
                      <p className="mt-1 text-sm text-red-500">{errors.company}</p>
                    )}
                  </div>
                </>
              )}

              {/* Email Field */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 rounded-xl border transition-all duration-200 ${
                    errors.email
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                      : darkMode
                      ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                      : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                  } focus:ring-4 focus:outline-none`}
                  placeholder="you@company.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`w-full px-4 py-3 pr-12 rounded-xl border transition-all duration-200 ${
                      errors.password
                        ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                        : darkMode
                        ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                        : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                    } focus:ring-4 focus:outline-none`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute inset-y-0 right-0 flex items-center pr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-500">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field (Sign Up) */}
              {isSignUp && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                      className={`w-full px-4 py-3 pr-12 rounded-xl border transition-all duration-200 ${
                        errors.confirmPassword
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                          : darkMode
                          ? 'border-gray-600 bg-gray-700 text-white focus:border-indigo-500 focus:ring-indigo-500/20'
                          : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-indigo-500/20'
                      } focus:ring-4 focus:outline-none`}
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className={`absolute inset-y-0 right-0 flex items-center pr-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              {!isSignUp && (
                <div className="flex items-center justify-between">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={(e) => handleInputChange('rememberMe', e.target.checked)}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <span className={`ml-2 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      Remember me
                    </span>
                  </label>
                  <a
                    href="/forgot-password"
                    className="text-sm text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Forgot password?
                  </a>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-200 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-4 focus:ring-indigo-500/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isSignUp ? 'Creating Account...' : 'Signing In...'}
                  </>
                ) : (
                  <>
                    {isSignUp ? 'Create Account' : 'Sign In'}
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Switch Mode */}
            <div className="text-center mt-6">
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {isSignUp ? 'Already have an account?' : "Need an account?"}
                <button
                  type="button"
                  onClick={() => {
                    if (!signupDisabled || isSignUp) {
                      setIsSignUp(!isSignUp);
                      setErrors({});
                      setFormData({
                        email: '',
                        password: '',
                        confirmPassword: '',
                        firstName: '',
                        lastName: '',
                        company: '',
                        rememberMe: false,
                      });
                    }
                  }}
                  className={`ml-1 font-medium ${
                    signupDisabled && !isSignUp
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-indigo-600 hover:text-indigo-500'
                  }`}
                  disabled={signupDisabled && !isSignUp}
                >
                  {isSignUp ? 'Sign in' : (signupDisabled ? 'Contact Administrator' : 'Create account')}
                </button>
              </p>
              {signupDisabled && !isSignUp && (
                <div className={`text-xs mt-2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  🔒 Account creation is administrator-controlled<br/>
                  <a href="https://www.idliapam.in" target="_blank" rel="noopener noreferrer" 
                     className="text-indigo-500 hover:text-indigo-400 underline">
                    Contact Admin: admin@idliapam.in
                  </a>
                </div>
              )}
            </div>

            {/* Security Badge */}
            <div className="text-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className={`flex items-center justify-center text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Secured by 256-bit SSL encryption
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Google OAuth Setup Modal */}
      <GoogleAuthSetupModal 
        isOpen={showGoogleSetupModal}
        onClose={() => setShowGoogleSetupModal(false)}
      />
    </div>
  );
};

export default Login;